import { z } from 'zod';

/**
 * Tag shape (spec 9.1). Mirrors `TAG_PATTERN` in `@cp/core/media-vocab`; the
 * schemas package deliberately has no dependency on core, so the pattern lives
 * in both places and a spec pins them together.
 */
export const TagSchema = z
  .string()
  .min(1)
  .max(40)
  .regex(/^[\p{Ll}\p{Nd}-]+$/u);

export const HotelMediaTagListSchema = z.object({ tags: z.array(TagSchema).max(20) });
export type HotelMediaTagList = z.infer<typeof HotelMediaTagListSchema>;

export const RightsBasisSchema = z.enum([
  'client_provided',
  'stock',
  'own_shoot',
  'ai_generated',
  'written_permission',
  'unknown',
]);
export const AiInvolvementSchema = z.enum(['none', 'ai_edited', 'ai_generated', 'unknown']);
export const PeopleConsentSchema = z.enum(['none', 'confirmed', 'crowd_exception']);
export const OrientationSchema = z.enum(['portrait', 'square', 'landscape', 'panorama']);
export const ComplianceStatusSchema = z.enum(['attachable', 'blocked', 'unknown']);
export const AnalysisStatusSchema = z.enum(['none', 'queued', 'running', 'done', 'failed']);

/**
 * Upload takes the batch defaults, so the fields carry `.default(null)` and a
 * missing field legitimately means "not given".
 */
export const MediaComplianceFieldsSchema = z.object({
  rightsBasis: RightsBasisSchema,
  rightsConfirmedAt: z.string().datetime().nullable().default(null),
  rightsConfirmedRef: z.string().max(500).nullable().default(null),
  aiInvolvement: AiInvolvementSchema,
  aiChangeNote: z.string().max(1000).nullable().default(null),
  peopleRecognizable: z.boolean().nullable().default(null),
  peopleConsent: PeopleConsentSchema.nullable().default(null),
  stockPlatform: z.string().max(200).nullable().default(null),
  stockTerritory: z.string().max(200).nullable().default(null),
  licenseExpiresAt: z.string().datetime().nullable().default(null),
});
export type MediaComplianceFields = z.infer<typeof MediaComplianceFieldsSchema>;

/**
 * Partial update must NOT carry defaults: Zod fills defaults during parse, so a
 * caption-only patch would come back with every compliance key present and null,
 * wiping the row. Every field here is a plain `.optional()`, and `undefined`
 * means "leave this column alone".
 */
export const MediaComplianceFieldsPatchSchema = z.object({
  rightsBasis: RightsBasisSchema.optional(),
  rightsConfirmedAt: z.string().datetime().nullable().optional(),
  rightsConfirmedRef: z.string().max(500).nullable().optional(),
  aiInvolvement: AiInvolvementSchema.optional(),
  aiChangeNote: z.string().max(1000).nullable().optional(),
  peopleRecognizable: z.boolean().nullable().optional(),
  peopleConsent: PeopleConsentSchema.nullable().optional(),
  stockPlatform: z.string().max(200).nullable().optional(),
  stockTerritory: z.string().max(200).nullable().optional(),
  licenseExpiresAt: z.string().datetime().nullable().optional(),
});
export type MediaComplianceFieldsPatch = z.infer<typeof MediaComplianceFieldsPatchSchema>;

export const HotelMediaSchema = z.object({
  id: z.string().guid(),
  hotelId: z.string().guid(),
  storageKey: z.string(),
  contentType: z.string(),
  sizeBytes: z.number().nullable(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  tags: z.array(z.string()),
  caption: z.string().nullable(),
  uploadedBy: z.string().guid().nullable(),
  createdAt: z.string().datetime(),
  url: z.string().url(),
  // W1 additions
  thumbUrl: z.string().url().nullable(),
  orientation: OrientationSchema.nullable(),
  rightsBasis: RightsBasisSchema,
  rightsConfirmedAt: z.string().datetime().nullable(),
  rightsConfirmedRef: z.string().nullable(),
  aiInvolvement: AiInvolvementSchema,
  aiChangeNote: z.string().nullable(),
  peopleRecognizable: z.boolean().nullable(),
  peopleConsent: PeopleConsentSchema.nullable(),
  stockPlatform: z.string().nullable(),
  stockTerritory: z.string().nullable(),
  licenseExpiresAt: z.string().datetime().nullable(),
  hasC2pa: z.boolean().nullable(),
  hasGps: z.boolean().nullable(),
  analysisStatus: AnalysisStatusSchema,
  attachable: z.boolean(),
  complianceStatus: ComplianceStatusSchema,
  blockers: z.array(z.string()),
  warnings: z.array(z.string()),
});
export type HotelMedia = z.infer<typeof HotelMediaSchema>;

/** `?tags=a&tags=b` and `?tags=a` both arrive; normalise to an array. */
const TagsQuery = z
  .union([TagSchema, z.array(TagSchema)])
  .optional()
  .transform((v) => (v === undefined ? undefined : Array.isArray(v) ? v : [v]));

export const ListHotelMediaQuerySchema = z.object({
  tags: TagsQuery,
  orientation: OrientationSchema.optional(),
  complianceStatus: ComplianceStatusSchema.optional(),
  aiInvolvement: AiInvolvementSchema.optional(),
  q: z.string().min(2).max(200).optional(),
  /**
   * `<ISO timestamp>|<uuid>` taken from the last row of the previous page. The
   * shape is validated here so a malformed value fails as a 400 at the pipe
   * instead of reaching Postgres, where a failed `::timestamptz` cast would
   * surface as a 500 and be logged as a server error.
   *
   * Up to SIX fractional digits, not three: the service emits the timestamp
   * at full `timestamptz` precision (microseconds), because a millisecond
   * cursor silently drops every row sharing the last row's millisecond — see
   * `CURSOR_TS_SQL` in `hotel-media.service.ts`. A regex that rejected the
   * service's own output would turn "több betöltése" into a 400. Fewer
   * digits still parse, so cursors already in flight keep working.
   */
  cursor: z
    .string()
    .regex(
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?Z\|[0-9a-fA-F-]{36}$/,
      'cursor must be "<ISO timestamp>|<uuid>"',
    )
    .optional(),
  limit: z.coerce.number().int().min(1).max(200).default(60),
});
export type ListHotelMediaQuery = z.infer<typeof ListHotelMediaQuerySchema>;

/** Cursor-paginated response for `GET /hotels/:hotelId/media` (spec 7./13.). */
export const ListHotelMediaResponseSchema = z.object({
  data: z.array(HotelMediaSchema),
  nextCursor: z.string().nullable(),
});
export type ListHotelMediaResponse = z.infer<typeof ListHotelMediaResponseSchema>;

/**
 * Built on `MediaComplianceFieldsPatchSchema` (not the defaulted
 * `MediaComplianceFieldsSchema`) — see the doc comment above that schema.
 * Using `.partial()` on the defaulted schema would fill every omitted field
 * with `null` during parse, so `{ caption: 'x' }` would come back carrying
 * every compliance key set to `null` and the empty-patch refine below would
 * never fire, silently wiping the row's compliance fields.
 */
export const PatchHotelMediaSchema = MediaComplianceFieldsPatchSchema.extend({
  caption: z.string().max(2000).nullable().optional(),
  tags: z.array(TagSchema).max(20).optional(),
}).refine((v) => Object.keys(v).length > 0, { message: 'empty patch' });
export type PatchHotelMedia = z.infer<typeof PatchHotelMediaSchema>;

export const BulkMediaOpSchema = z
  .object({
    ids: z.array(z.string().guid()).min(1).max(200),
    op: z.enum(['set_compliance', 'add_tags', 'remove_tags', 'delete']),
    tags: z.array(TagSchema).max(20).optional(),
    // Uses the non-defaulted patch schema for the same reason as
    // `PatchHotelMediaSchema`: a bulk "add these tags" op with no `compliance`
    // payload must not silently materialise nulled-out compliance fields.
    compliance: MediaComplianceFieldsPatchSchema.optional(),
  })
  .superRefine((v, ctx) => {
    if ((v.op === 'add_tags' || v.op === 'remove_tags') && (!v.tags || v.tags.length === 0)) {
      ctx.addIssue({ code: 'custom', message: 'tags required for this op', path: ['tags'] });
    }
    if (v.op === 'set_compliance' && !v.compliance) {
      ctx.addIssue({
        code: 'custom',
        message: 'compliance required for this op',
        path: ['compliance'],
      });
    }
  });
export type BulkMediaOp = z.infer<typeof BulkMediaOpSchema>;

export const OverrideReasonSchema = z.enum([
  'rights_verified_offline',
  'consent_verified_offline',
  'other',
]);
export const AttachOverrideSchema = z.object({
  reason: OverrideReasonSchema,
  // A written reason must actually say something: a whitespace-only string
  // (spaces, tabs, newlines) must not satisfy the note-required gate for a
  // child-tagged override without confirmed consent (checked with a plain
  // truthiness test in the service — "   " is truthy, so the schema is what
  // has to reject it).
  note: z
    .string()
    .max(1000)
    .refine((v) => v.trim().length > 0, { message: 'note must not be blank' })
    .optional(),
});
export type AttachOverride = z.infer<typeof AttachOverrideSchema>;

/**
 * `DELETE /hotels/:hotelId/media/:id?detach=true` — the second step of the
 * two-step delete. Without it a picture that a post still uses answers 409
 * `media_in_use` (with `postCount`); with it the post rows referencing the
 * picture are removed first, then the picture. Only the literal `true`
 * switches it on: a typo must not silently take a post's image away.
 */
export const RemoveHotelMediaQuerySchema = z.object({
  detach: z
    .enum(['true', 'false'])
    .default('false')
    .transform((v) => v === 'true'),
});
export type RemoveHotelMediaQuery = z.infer<typeof RemoveHotelMediaQuerySchema>;
